import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Url } from '@wix/da-url';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { ReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import TooltipSisuContent from '../TooltipSisuContent/TooltipSisuContent';
import { getSisuActionKey } from '../SisuActionPopover/actionKey';

import s from './SisuAnonMenuPopoverContext.scss';

export interface SisuAnonMenuPopoverContextValue {
  openSisuAnonPopover: (referral: ReferralType) => void;
}

const defaultValue: SisuAnonMenuPopoverContextValue = {
  openSisuAnonPopover: () => {},
};

export const SisuAnonMenuPopoverContext =
  createContext<SisuAnonMenuPopoverContextValue>(defaultValue);

export function SisuAnonMenuPopoverProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const isSisuPopoverEnabled = useFlag('sisu_popover');
  const [referral, setReferral] = useState<ReferralType | null>(null);
  const { sendUserToJoinPage } = useRestrictToLoggedIn();

  const openSisuAnonPopover = useCallback(
    (r: ReferralType) => {
      if (isSisuPopoverEnabled) {
        setReferral(r);
      }
    },
    [isSisuPopoverEnabled]
  );

  const onClose = useCallback(() => setReferral(null), []);

  const goToJoinPage = useCallback(() => {
    if (referral) {
      sendUserToJoinPage(referral);
    }
    setReferral(null);
  }, [referral, sendUserToJoinPage]);

  const goToLoginPage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const base = Url.loginLink();
    const sep = base.includes('?') ? '&' : '?';
    window.location.assign(
      `${base}${sep}referer=${encodeURIComponent(window.location.href)}`
    );
    setReferral(null);
  }, []);

  const value = useMemo(
    () => ({ openSisuAnonPopover }),
    [openSisuAnonPopover]
  );

  const overlay =
    isSisuPopoverEnabled &&
    referral &&
    typeof document !== 'undefined' &&
    createPortal(
      <div className={s['overlay']} role="presentation" onClick={onClose}>
        <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
          <ReverseTheme>
            <div className={s['panel']}>
              <TooltipSisuContent
                actionKey={getSisuActionKey(referral)}
                onLoginClick={goToLoginPage}
                onJoinClick={goToJoinPage}
              />
            </div>
          </ReverseTheme>
        </div>
      </div>,
      document.body
    );

  return (
    <SisuAnonMenuPopoverContext.Provider value={value}>
      {children}
      {overlay}
    </SisuAnonMenuPopoverContext.Provider>
  );
}

export function useSisuAnonMenuPopover(): SisuAnonMenuPopoverContextValue {
  return useContext(SisuAnonMenuPopoverContext);
}
